'use client';

import Image from 'next/image';
import { useLanguage } from '@/components/language-provider';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { 
  Building2, 
  Clock, 
  MessageSquare,
  QrCode
} from 'lucide-react';

export default function ContactPage() {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent" />
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <Badge variant="outline" className="mb-6 px-4 py-1.5 border-primary/30 text-primary">
              {t('contact.info.title')}
            </Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
              {t('contact.title')}
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
              {t('contact.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 md:py-16">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Left Column - Contact Info & QR Codes */}
            <div className="space-y-8">
              {/* Company Info */}
              <Card className="border-border/50">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-primary" />
                    </div>
                    <CardTitle>{t('contact.info.title')}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="font-semibold text-lg">{t('contact.info.company')}</p>
                    <p className="text-muted-foreground">{t('contact.info.address')}</p>
                    <p className="text-muted-foreground">{t('contact.info.factory')}</p>
                  </div>
                </CardContent>
              </Card>

              {/* QR Codes */}
              <Card className="border-border/50">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <QrCode className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <CardTitle>{t('contact.qr.title')}</CardTitle>
                      <p className="text-sm text-muted-foreground mt-1">{t('contact.qr.desc')}</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-6">
                    {/* WeChat QR */}
                    <div className="text-center">
                      <div className="relative w-40 h-40 mx-auto bg-white rounded-lg p-2 shadow-sm border">
                        <Image
                          src="/wechat-qr.jpg"
                          alt={t('contact.qr.wechat')}
                          fill
                          className="object-contain"
                        />
                      </div>
                      <p className="mt-3 font-medium text-foreground">{t('contact.qr.wechat')}</p>
                      <p className="text-sm text-muted-foreground">WeChat</p>
                    </div>
                    
                    {/* WhatsApp QR */}
                    <div className="text-center">
                      <div className="relative w-40 h-40 mx-auto bg-white rounded-lg p-2 shadow-sm border">
                        <Image
                          src="/whatsapp-qr.jpg"
                          alt={t('contact.qr.whatsapp')}
                          fill
                          className="object-contain"
                        />
                      </div>
                      <p className="mt-3 font-medium text-foreground">{t('contact.qr.whatsapp')}</p>
                      <p className="text-sm text-muted-foreground">WhatsApp</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Business Hours */}
              <Card className="border-border/50">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Clock className="w-5 h-5 text-primary" />
                    </div>
                    <CardTitle>{t('contact.hours.title')}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  <p className="text-foreground">{t('contact.hours.weekdays')}</p>
                  <p className="text-muted-foreground">{t('contact.hours.weekend')}</p>
                </CardContent>
              </Card>

              {/* Response Time */}
              <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <MessageSquare className="w-5 h-5 text-primary" />
                    </div>
                    <CardTitle>{t('contact.response.title')}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">{t('contact.response.desc')}</p>
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Contact Form */}
            <Card className="border-border/50 h-fit">
              <CardHeader>
                <CardTitle className="text-xl">{t('contact.form.title')}</CardTitle>
              </CardHeader>
              <CardContent>
                <form className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label htmlFor="name" className="text-sm font-medium text-foreground">
                        {t('contact.form.name')}
                      </label>
                      <Input 
                        id="name" 
                        placeholder={t('contact.form.name')}
                        className="border-border/50"
                      />
                    </div>
                    <div className="space-y-2">
                      <label htmlFor="email" className="text-sm font-medium text-foreground">
                        {t('contact.form.email')}
                      </label>
                      <Input 
                        id="email" 
                        type="email" 
                        placeholder={t('contact.form.email')}
                        className="border-border/50"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <label htmlFor="company" className="text-sm font-medium text-foreground">
                      {t('contact.form.company')}
                    </label>
                    <Input 
                      id="company" 
                      placeholder={t('contact.form.company')}
                      className="border-border/50"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label htmlFor="subject" className="text-sm font-medium text-foreground">
                      {t('contact.form.subject')}
                    </label>
                    <Input 
                      id="subject" 
                      placeholder={t('contact.form.subject')}
                      className="border-border/50"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label htmlFor="message" className="text-sm font-medium text-foreground">
                      {t('contact.form.message')}
                    </label>
                    <Textarea 
                      id="message" 
                      placeholder={t('contact.form.message')}
                      rows={5}
                      className="border-border/50 resize-none"
                    />
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full bg-primary hover:bg-primary/90"
                    size="lg"
                  >
                    {t('contact.form.submit')}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
}
